import { addMinutes, endOfDay, format, parseISO, startOfDay, startOfWeek, toDate } from 'date-fns';
import { utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz';
import * as moment from 'moment';
import { Load } from '../global/interfaces/load';
import { Driver } from '../global/interfaces/driver';
import { Job } from '../global/interfaces/job';
import { Country } from '../global/interfaces/country';
import { Injectable } from '@angular/core';
import { NgbDateStruct, NgbTimeStruct } from '@ng-bootstrap/ng-bootstrap';
import { StopTypeEnum, StopTypeEnumLowercase } from '../global/interfaces/stopTypeEnum';
import { Stop } from '../global/interfaces/stop';
import { BootstrapDate, BootstrapTime } from '../global/types/BootstrapDateTime';
import { JobFlagType } from '../global/enums/JobFlagType';
import { JobTypeEnum } from '../global/enums/JobTypeEnum';
import { Status } from '../global/enums/Status';
import { AbstractControl, ValidatorFn } from '@angular/forms';
import { AuthService } from '../global/services/auth.service';
import { DistanceUnit } from '../global/enums/DistanceUnit';
import { ContractJob } from '../global/interfaces/contractJob';
import { ProjectEnvironment } from '../global/types/ProjectEnvironment';
import { Projects } from '../global/types/Projects';
import { environment } from '../../environments/environment';
import { ContractJobMaster } from '../global/types/ContractJobMasterTypes';
import { HostLocationService } from '../global/services/hostLocation.service';

export interface DateTitle {
  day: string;
  date: Date;
}

@Injectable()
export class HelperFunctions {
  constructor(private authService: AuthService, private hostLocationService: HostLocationService) {}

  daysOfTheWeek = {
    0: 'Sunday',
    1: 'Monday',
    2: 'Tuesday',
    3: 'Wednesday',
    4: 'Thursday',
    5: 'Friday',
    6: 'Saturday',
  };
  sixDays = 1000 * 60 * 60 * 24 * 6;
  minute = 1000 * 60;
  hour = 60 * this.minute;
  day = 24 * this.hour;

  formatDateWithDateFns(date: Date | string, dateFormat: string) {
    return format(new Date(date), dateFormat);
  }

  formatDateToTz(date: Date | string, dateFormat: string, timezone: string) {
    return format(utcToZonedTime(date, timezone), dateFormat);
  }

  formatDate(date, format = 'DD.MM.YYYY') {
    if (moment(date).isValid()) {
      return moment(date).format(format);
    } else {
      return '';
    }
  }
  formatTime(date) {
    if (moment(date).isValid()) {
      return moment(date).format('HH:mm');
    }
  }
  formatChatDate(date) {
    if (moment(date).isValid()) {
      const now = moment().format('LLLL');
      const diff = moment(now).diff(date, 'days');
      if (diff < 1) {
        return moment(date).format('HH:mm');
      } else if (diff >= 1 && diff <= 7) {
        return moment(date).format('dddd HH:mm');
      } else if (diff > 7) {
        return moment(date).format('DD.MM.YYYY HH:mm');
      }
    }
  }

  /**
   * Converts date objects from bootstrap components to iso string
   *
   * @returns Iso string
   */

  createNewDate(date, time): string {
    const { year, month, day } = date;
    const { hour, minute, second } = time;

    const dateWithoutTime = new Date().setFullYear(year, month - 1, day);
    const fullNewDate = new Date(dateWithoutTime).setHours(hour, minute, second);
    // TODO: Timezone sensitive, puts in browser's timezone!
    return new Date(fullNewDate).toISOString();
  }

  createDate(date: NgbDateStruct, time: NgbTimeStruct): Date {
    const { year, month, day } = date;
    const { hour, minute, second } = time;

    const dateWithoutTime = new Date().setFullYear(year, month - 1, day);
    const fullNewDate = new Date(dateWithoutTime).setUTCHours(hour, minute, second);
    // TODO: Timezone sensitive, puts in browser's timezone!
    return new Date(fullNewDate);
  }

  createStopDate(date: NgbDateStruct, time: NgbTimeStruct, timezone?: string): Date {
    const { year, month, day } = date;
    const { hour, minute, second } = time;

    const dateWithoutTime = new Date().setFullYear(year, month - 1, day);
    const fullNewDate = new Date(dateWithoutTime).setHours(hour, minute, second, 0);
    return timezone ? this.zonedTimeToUtc(new Date(fullNewDate), timezone) : new Date(fullNewDate);
  }

  zonedTimeToUtc(date: Date, timezone: string) {
    return zonedTimeToUtc(date, timezone);
  }

  utcToZonedDate(date: Date | string, tz: string): Date {
    return utcToZonedTime(date, tz);
  }

  createNewZonedDateOrReturnNull(timezone: string, utcDate?: Date | string): Date | null {
    return utcDate ? this.utcToZonedDate(utcDate, timezone) : null;
  }

  createNewUTCDate(
    data: {
      year?: number;
      month?: number;
      day?: number;
      hour?: number;
      minute?: number;
      second?: number;
      millisecond?: number;
    } = {},
  ): Date {
    data.year = data.year ?? 0;
    data.month = data.month ?? 1;
    data.day = data.day ?? 1;
    data.hour = data.hour ?? 0;
    data.minute = data.minute ?? 0;
    data.second = data.second ?? 0;
    data.millisecond = data.millisecond ?? 0;

    return new Date(
      Date.UTC(data.year, data.month - 1, data.day, data.hour, data.minute, data.second, data.millisecond),
    );
  }

  convertDateToUTC(date: Date): Date {
    return toDate(
      Date.UTC(
        date.getFullYear(),
        date.getMonth(),
        date.getDate(),
        date.getHours(),
        date.getMinutes(),
        date.getSeconds(),
      ),
    );
  }

  setTimezoneHoursAndReturnUtc = (
    date: Date,
    timezone: string,
    { hour, minute }: { hour: number; minute: number },
  ): Date => {
    const dateInTz = this.utcToZonedDate(date, timezone);
    const dateInTzWithSetTime = new Date(dateInTz.setHours(hour, minute));
    return zonedTimeToUtc(dateInTzWithSetTime, timezone);
  };

  // Helper function to format date as in "Tuesday 13.08.2024. 14:00"
  formatWithWeekDay = (date: Date, timezone: string) => {
    const options: Intl.DateTimeFormatOptions = {
      weekday: 'long',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone: timezone,
    };
    return new Date(date).toLocaleString('en-GB', options).replace(/\//g, '.');
  };

  isDateInTheFuture(date: NgbDateStruct, time: NgbTimeStruct): boolean {
    if (!date || !time) {
      return false;
    }
    const selectedDateTime = this.createDate(date, time).getTime();

    const now = new Date().getTime();

    return selectedDateTime > now;
  }

  areSameDate(date1: Date, date2: Date): boolean {
    return (
      date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getDate() === date2.getDate()
    );
  }

  dateToDateObject(date: Date): BootstrapDate {
    return { day: date.getDate(), month: date.getMonth() + 1, year: date.getFullYear() };
  }

  dateToTimeObject(date: Date): BootstrapTime {
    return { hour: date.getHours(), minute: date.getMinutes(), second: date.getSeconds() };
  }

  utcDateToTimeObject(date: Date): BootstrapTime {
    return { hour: date.getUTCHours(), minute: date.getUTCMinutes(), second: date.getUTCSeconds() };
  }

  dateObjectToDate({ year, month, day }: NgbDateStruct): Date {
    return this.createNewUTCDate({ year, month, day, hour: 0, minute: 0, second: 0, millisecond: 0 });
  }

  metersToMiles(meters): number {
    return parseFloat((meters / 1609.344).toFixed(1));
  }

  metersToKM(meters): number {
    return parseFloat((meters * 0.001).toFixed(1));
  }

  bucksPerDistance(price, distance): number {
    const preferredDistanceUnit = this.authService.getDistanceUnit();
    const number =
      preferredDistanceUnit === DistanceUnit.mile ? this.metersToMiles(distance) : this.metersToKM(distance);
    return number > 0 ? parseFloat((price / number).toFixed(1)) : 0;
  }

  hasLoadInfo(load: Load): boolean {
    return (
      !!load.weightUnit ||
      !!load.weight ||
      !!load?.measures?.height ||
      !!load?.measures?.length ||
      !!load?.measures?.width
    );
  }

  /**
   * Difference between actual and planned date
   */

  minutesDifference(actual, planned) {
    if (actual) {
      const diff = Math.abs(actual - planned) / 1000;
      const days = Math.floor(diff / 86400);
      const hours = Math.floor(diff / 3600) % 24;
      const minutes = Math.floor(diff / 60) % 60;
      const res =
        (days ? days + 'd' : '') +
        (hours ? hours + 'h' : '') +
        ((!days && !hours && minutes > 29) || days || hours ? minutes + 'min' : '');
      return res;
    }
  }

  minutesToHoursAndDays(time: number) {
    const days = Math.floor(time / 1440);
    const hours = Math.floor(time / 60) % 24;
    const minutes = Math.floor(time) % 60;
    const res =
      (days ? days + 'd ' : '') +
      (hours ? hours + 'h ' : '') +
      ((!days && !hours && minutes) || days || hours ? minutes + 'min' : '');
    return res;
  }

  uniqueDrivers(job: Job): Driver[] {
    const drivers = [];
    const driverMap = {};
    for (const stop of job.stops) {
      const crews = stop?.crews as any;
      for (const crew of crews) {
        if (crew?.drivers) {
          for (const driver of crew.drivers) {
            if (!driverMap[driver.id]) {
              driverMap[driver.id] = true;
              drivers.push(driver);
            }
          }
        }
      }
    }
    return drivers;
  }
  setDaysScaleTitle() {
    const day = this.sixDays / 6;
    const days: DateTitle[] = [];
    for (let i = 0; i < 14; i++) {
      days[i] = {
        day: this.daysOfTheWeek[new Date(new Date().getTime() + i * day - this.sixDays).getDay()],
        date: new Date(new Date().getTime() + i * day - this.sixDays),
      };
    }
    return days;
  }
  setHourScaleTitle() {
    const hours: number[] = [];
    for (let i = 0; i < 24; i++) {
      hours[i] = i;
    }
    return hours;
  }
  returnTimeStart() {
    return Math.floor((new Date(new Date().setHours(0, 0, 0, 0)).getTime() - this.sixDays) / this.minute);
  }
  returnCountryIso(counties: Country[], iso: string) {
    return counties.filter((country) => country.iso === iso).map((country) => country);
  }
  isJobInPast(plannedStartDate) {
    const todayTime = new Date().getTime();
    const jobPickupTime = new Date(plannedStartDate).getTime();
    return todayTime > jobPickupTime;
  }

  findFirstDeliveryStopPlannedDate(job: Job): string | Date {
    const stops = job.stops;
    for (const stop of stops) {
      if (stop.type === StopTypeEnumLowercase.delivery) {
        return stop.plannedStartDate;
      }
    }
    return stops[stops.length - 1].plannedStartDate;
  }

  firstStop(job: Job): Stop {
    return job?.stops[0];
  }

  lastStop(job: Job): Stop {
    return (job?.stops)[job?.stops.length - 1];
  }

  hasArchivedFlag(job: Job) {
    return job.flags?.some((f) => f.type === JobFlagType.archived);
  }

  isJobArchived(job: Job): boolean {
    // Support for older jobs with archived status
    return this.hasArchivedFlag(job) || ['Archived'].includes(job.status);
  }

  is24HourTimeFormat(timeFormat: string): boolean {
    return timeFormat === 'HH:mm';
  }

  isFalsyValue(value: any) {
    return [null, undefined, ''].includes(value);
  }

  isNumberAndInRange(num: number, start: number, end: number): boolean {
    if (this.isFalsyValue(num)) return false;
    return num >= start && num <= end;
  }

  isHourIn24HourRange(hour: number): boolean {
    return this.isNumberAndInRange(hour, 0, 23);
  }

  isMinuteInRange(minute: number): boolean {
    return this.isNumberAndInRange(minute, 0, 59);
  }

  /**
   * Returns array of numbers from 1 to number
   * @param number Length of the array
   * @param offset if defined, will offset all numbers in array by the provided number
   */
  generateRange(number: number, offset = 0): Array<number> {
    return [...new Array<number>(number)].map((n, i) => i + 1 + offset);
  }

  isTraction(job: Job): boolean {
    return job.type === JobTypeEnum.traction;
  }

  startOfDay(date: Date): Date {
    return startOfDay(date);
  }
  startOfWeek(date: Date, firstDay: 0 | 1 | 2 | 3 | 4 | 5 | 6 = 1): Date {
    return startOfWeek(date, { weekStartsOn: firstDay });
  }
  endOfDay(date: Date): Date {
    return endOfDay(date);
  }

  isJobCancelled(status: Status | string) {
    return [Status.cancelledShipper, Status.cancelled, Status.cancelledHaulier].includes(status as Status);
  }
  createArrayFromEnum(enumObject): string[] {
    const all = Object.keys(enumObject).map((key) => enumObject[key]);
    return all;
  }

  // decides whether a form filed should be required based on a given condition
  conditionalValidator(condition: () => boolean, validator: ValidatorFn): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } => {
      if (!condition()) {
        return null;
      }
      return validator(control);
    };
  }

  populateDropdownList(
    items: any[],
    titlePrefix?: string,
    getValueFn: (item: any) => any = (item) => item.id,
  ): { title: string; value: any }[] {
    return items.map((item) => {
      const title = titlePrefix ? `${titlePrefix}: ${item.name}` : item.name;
      const value = getValueFn(item);
      return { title, value };
    });
  }

  formatFloatToDecimal(number: number, decimal: number) {
    return parseFloat(number.toFixed(decimal));
  }

  isoToDate(isoString: string): Date {
    const date = parseISO(isoString);
    const utcDate = addMinutes(date, date.getTimezoneOffset());
    return utcDate;
  }

  pullLoadingSlotDataFromContractJob(contractJob: ContractJob): string {
    const { slots, stops } = contractJob;
    if (!slots.length) return;
    const loadingSlot = slots.find((slot) => slot.type === 'loading');
    const timezone = stops.find((stop) => stop.sequenceId === 0)?.timezone;
    const dateFormat = `EEEE ${this.authService.getDateFormat()} ${this.authService.getTimeFormat()}`;

    const startDate = this.utcToZonedDate(loadingSlot?.startDate, timezone);
    const endDate = this.utcToZonedDate(loadingSlot?.endDate, timezone);

    const startDateFormatted = this.formatDateWithDateFns(startDate, dateFormat);

    const isSameDay = startDate.toDateString() === endDate.toDateString();

    const endDateFormatted = this.formatDateWithDateFns(
      endDate,
      isSameDay ? this.authService.getTimeFormat() : dateFormat,
    );

    return `${startDateFormatted} - ${endDateFormatted}, Gate: ${loadingSlot?.gate?.name ?? 'Not specified'}`;
  }

  formatTimeOnly = (date: Date, timezone: string) => {
    const options: Intl.DateTimeFormatOptions = {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone: timezone,
    };
    return new Date(date).toLocaleString('en-GB', options);
  };

  getEnvironmentAndProject(host: string): { environment: ProjectEnvironment; project: Projects } {
    const hostMap: Record<string, { environment: ProjectEnvironment; project: Projects }> = {
      'app.sit.eyeqmarketplace.com': { environment: ProjectEnvironment.SIT, project: Projects.WINCANTON },
      'app.uat.eyeqmarketplace.com': { environment: ProjectEnvironment.UAT, project: Projects.WINCANTON },
      'app.phase3.eyeqmarketplace.com': { environment: ProjectEnvironment.PHASE3, project: Projects.WINCANTON },
      'app.live.eyeqmarketplace.com': { environment: ProjectEnvironment.PROD, project: Projects.WINCANTON },
      'app.apollo-uat.zeuslabs.com': { environment: ProjectEnvironment.UAT, project: Projects.APOLLO },
      'atom.zeuslabs.com': { environment: ProjectEnvironment.PROD, project: Projects.APOLLO },
      'app.zeuslabs.com': { environment: ProjectEnvironment.PROD, project: Projects.ZEUS },
      'app.sit.zeuslabs.com': { environment: ProjectEnvironment.SIT, project: Projects.ZEUS },
      'app.uat.zeuslabs.com': { environment: ProjectEnvironment.UAT, project: Projects.ZEUS },
      'ipl.zeuslabs.com': { environment: ProjectEnvironment.PROD, project: Projects.IPL },
      'zeus.local': {
        environment: environment.projectEnvironment as ProjectEnvironment,
        project: environment.project as Projects,
      },
    };

    const defaultValues = { environment: ProjectEnvironment.PROD, project: Projects.ZEUS };

    return hostMap[host] || defaultValues;
  }

  isProject() {
    const host = this.hostLocationService.getHost();
    return this.getEnvironmentAndProject(host).project;
  }
}
